/**
 * The sending side. Once a frame (tick), each entity in anyone's view is
 * canonicalised: every field's stored form is compared with the last, and
 * the frame it changed is remembered. A peer that last saw the entity at
 * frame N is then sent exactly the fields that changed after N, with no
 * per-peer comparing, and the bytes of that delta are encoded once and
 * reused for every peer in the same position (which, in steady state, is
 * all of them).
 *
 * Assumes a reliable, ordered transport (a WebSocket): the peer always has
 * everything sent to it before, so nothing is acknowledged or repeated.
 */
import { Writer } from "./bytes";
import type { Entity, EntityOf, Id, Protocol, Types } from "./schema";

/** The first byte of every packet, for a decoder to check. */
export const FORMAT = 1;

interface Rec {
  /** Each field's stored form, and the frame it last changed. */
  stored: unknown[];
  changedAt: number[];
  /** The frame it was last in anyone's view; a record nobody asked for last frame is dropped. */
  seen: number;
  /** Its fields encoded whole, and encoded as a delta since each frame asked for: filled in as peers ask, cleared when it changes. */
  full: Uint8Array | null;
  deltas: { since: number; bytes: Uint8Array }[];
}

interface Known {
  handle: number;
  lastSent: number;
}

/** What one peer holds of one entity type: which entities (by handle) and when it last heard of each. */
class PeerTable {
  readonly known = new Map<Id, Known>();
  private readonly free: number[] = [];
  private next = 0;

  alloc(): number {
    return this.free.length ? this.free.pop()! : this.next++;
  }

  release(handle: number): void {
    this.free.push(handle);
  }

  reset(): void {
    this.known.clear();
    this.free.length = 0;
    this.next = 0;
  }
}

/**
 * One connection's view: which entities it has been sent and how up to date
 * each is. Make one per connection (`SnapshotServer.peer()`); `reset` it if
 * the far side starts afresh (a reconnect over the same object).
 */
export class Peer {
  /** @internal */
  readonly tables: PeerTable[];

  constructor(types: number) {
    this.tables = Array.from({ length: types }, () => new PeerTable());
  }

  reset(): void {
    for (const t of this.tables) t.reset();
  }
}

/** The entities of each type a peer is to see this frame. */
export type Visible<M extends Types> = { [K in keyof M]: Iterable<EntityOf<M[K]>> };

export class SnapshotServer<M extends Types> {
  /** The current frame: `begin` starts the next. */
  frame = 0;
  private readonly entities: Entity[];
  private readonly tables: Map<Id, Rec>[];
  private readonly writer = new Writer(4096);
  private readonly present = new Set<Id>();
  private readonly created: [Id, Rec][] = [];
  private readonly updated: [Known, Rec][] = [];
  private readonly removed: number[] = [];

  constructor(readonly protocol: Protocol<M>) {
    this.entities = protocol.names.map((n) => protocol.types[n]!);
    this.tables = this.entities.map(() => new Map());
  }

  peer(): Peer {
    return new Peer(this.entities.length);
  }

  /** Start a frame. Call once a tick, before encoding for any peer. */
  begin(): void {
    const stale = this.frame - 1;
    for (const table of this.tables) for (const [id, rec] of table) if (rec.seen < stale) table.delete(id);
    this.frame++;
  }

  /**
   * The packet for `peer`: what came into its view (whole), what changed in
   * the entities it already had, and what left. Each type's iterable is the
   * complete set it should see this frame; one left out is treated as empty.
   */
  encode(peer: Peer, visible: Visible<M>): Uint8Array {
    const frame = this.frame;
    const w = this.writer;
    w.reset();
    w.u8(FORMAT);
    for (let t = 0; t < this.entities.length; t++) {
      const ent = this.entities[t]!;
      const recs = this.tables[t]!;
      const pt = peer.tables[t]!;
      const { present, created, updated, removed } = this;
      present.clear();
      created.length = updated.length = removed.length = 0;

      for (const e of visible[this.protocol.names[t]!] ?? []) {
        const id = (e as Record<string, unknown>)[ent.idKey] as Id;
        present.add(id);
        let rec = recs.get(id);
        if (!rec) recs.set(id, (rec = this.make(ent, e)));
        else if (rec.seen !== frame) this.touch(ent, rec, e);
        const k = pt.known.get(id);
        if (!k) created.push([id, rec]);
        else if (changedSince(rec, k.lastSent)) updated.push([k, rec]);
      }
      // Gone from its view: say so, and free the handles (a create below may take one).
      for (const [id, k] of pt.known) {
        if (present.has(id)) continue;
        removed.push(k.handle);
        pt.known.delete(id);
        pt.release(k.handle);
      }

      w.uvar(removed.length);
      for (const h of removed) w.uvar(h);
      w.uvar(created.length);
      for (const [id, rec] of created) {
        const handle = pt.alloc();
        pt.known.set(id, { handle, lastSent: frame });
        w.uvar(handle);
        ent.idField.write(w, ent.idField.canon(id));
        this.writeFull(ent, rec, w);
      }
      w.uvar(updated.length);
      for (const [k, rec] of updated) {
        w.uvar(k.handle);
        this.writeDelta(ent, rec, k.lastSent, w);
        k.lastSent = frame;
      }
    }
    return w.finish();
  }

  private make(ent: Entity, e: object): Rec {
    const n = ent.fields.length;
    const stored = new Array<unknown>(n);
    const changedAt = new Array<number>(n).fill(this.frame);
    for (let i = 0; i < n; i++) stored[i] = ent.fields[i]!.canon((e as Record<string, unknown>)[ent.names[i]!]);
    return { stored, changedAt, seen: this.frame, full: null, deltas: [] };
  }

  /** Bring a record up to date with its entity: once a frame, whoever asks. */
  private touch(ent: Entity, rec: Rec, e: object): void {
    rec.seen = this.frame;
    let changed = false;
    for (let i = 0; i < ent.fields.length; i++) {
      const fld = ent.fields[i]!;
      const s = fld.canon((e as Record<string, unknown>)[ent.names[i]!]);
      if (fld.equals(rec.stored[i], s)) continue;
      rec.stored[i] = s;
      rec.changedAt[i] = this.frame;
      changed = true;
    }
    if (changed) {
      rec.full = null;
      rec.deltas.length = 0;
    }
  }

  private writeFull(ent: Entity, rec: Rec, w: Writer): void {
    if (rec.full) return w.bytes(rec.full);
    const start = w.pos;
    for (let i = 0; i < ent.fields.length; i++) ent.fields[i]!.write(w, rec.stored[i]);
    rec.full = w.slice(start);
  }

  private writeDelta(ent: Entity, rec: Rec, since: number, w: Writer): void {
    for (const d of rec.deltas) if (d.since === since) return w.bytes(d.bytes);
    const start = w.pos;
    writeMask(w, rec.changedAt, since);
    for (let i = 0; i < ent.fields.length; i++) if (rec.changedAt[i]! > since) ent.fields[i]!.write(w, rec.stored[i]);
    rec.deltas.push({ since, bytes: w.slice(start) });
  }
}

function changedSince(rec: Rec, since: number): boolean {
  for (const at of rec.changedAt) if (at > since) return true;
  return false;
}

/**
 * Which fields changed, as a bitset seven bits a byte with a continuation
 * bit, trailing empty bytes left off: with the busy fields first in the
 * schema, a typical delta's mask is one byte however many fields there are.
 */
function writeMask(w: Writer, changedAt: number[], since: number): void {
  let last = -1;
  for (let i = 0; i < changedAt.length; i++) if (changedAt[i]! > since) last = (i / 7) | 0;
  if (last < 0) return w.u8(0);
  for (let g = 0; g <= last; g++) {
    let byte = 0;
    for (let b = 0; b < 7; b++) {
      const i = g * 7 + b;
      if (i < changedAt.length && changedAt[i]! > since) byte |= 1 << b;
    }
    w.u8(g < last ? byte | 0x80 : byte);
  }
}
